import { useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLongPress } from 'react-use';
import { formatDate } from './date-utils';

const usePrevious = <T>(value: T): T => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T>(value);

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

const useIsMounted = (): (() => boolean) => {
  const isMounted = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return useCallback(() => isMounted.current, []);
};

const useDeepCompareMemo = <TKey, TValue>(
  memoFn: () => TValue,
  key: TKey
): TValue => {
  const ref = useRef<{ key: TKey; value: TValue }>();

  if (!ref.current || !isEqual(key, ref.current.key)) {
    ref.current = { key, value: memoFn() };
  }

  return ref.current.value;
};
const useIsViewing = (): boolean => {
  const [isViewing, setIsViewing] = useState<boolean>(false);

  useIonViewDidEnter(() => {
    setIsViewing(true);
  });

  useIonViewDidLeave(() => {
    setIsViewing(false);
  });

  return isViewing;
};

const getLocationHash = (): string | undefined => {
  if (window.location.hash && window.location.hash !== '') {
    return window.location.hash;
  }
};

const useLocationHash = (): string | undefined => {
  const isViewing = useIsViewing();
  const [hash, setHash] = useState<string | undefined>(undefined);

  useEffect(() => {
    setHash(getLocationHash());
  }, []);

  const listener = useCallback(() => {
    setHash(getLocationHash());
  }, []);

  useEffect(() => {
    if (isViewing) {
      window.addEventListener('hashchange', listener);
    } else {
      window.removeEventListener('hashchange', listener);
    }
  }, [isViewing, listener]);

  return hash;
};

interface CancellableLongPressOptions {
  isPreventDefault?: boolean;
  delay?: number;
  xThreshold?: number;
  yThreshold?: number;
}

interface CancellableLongPressReturnFunctions {
  onMouseDown: (e: unknown) => void;
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchEnd: () => void;
}

const useCancellableLongPress = (
  callback: (e: TouchEvent | MouseEvent) => void,
  options?: CancellableLongPressOptions | undefined
): CancellableLongPressReturnFunctions => {
  const { isPreventDefault, delay, xThreshold, yThreshold } = options ?? {};

  const isPotentialLongPress = useRef<boolean>(false);
  const initialX = useRef<number>(0);
  const initialY = useRef<number>(0);

  const wrappedCallback = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (isPotentialLongPress.current) {
        callback(e);
      }
      isPotentialLongPress.current = false;
    },
    [callback]
  );

  const longPressCallbacks = useLongPress(wrappedCallback, {
    isPreventDefault,
    delay,
  });

  const onMouseDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      isPotentialLongPress.current = true;
      longPressCallbacks.onMouseDown(e);
    },
    [longPressCallbacks]
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      isPotentialLongPress.current = true;
      initialX.current = e.targetTouches[0].clientX;
      initialY.current = e.targetTouches[0].clientY;
      longPressCallbacks.onTouchStart(e);
    },
    [longPressCallbacks]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (
        xThreshold !== undefined &&
        Math.abs(e.targetTouches[0].clientX - initialX.current) > xThreshold
      ) {
        isPotentialLongPress.current = false;
      }
      if (
        yThreshold !== undefined &&
        Math.abs(e.targetTouches[0].clientY - initialY.current) > yThreshold
      ) {
        isPotentialLongPress.current = false;
      }
    },
    [xThreshold, yThreshold]
  );

  const onMouseUp = useCallback(() => {
    isPotentialLongPress.current = false;
    longPressCallbacks.onMouseUp();
  }, [longPressCallbacks]);

  const onMouseLeave = useCallback(() => {
    isPotentialLongPress.current = false;
    longPressCallbacks.onMouseLeave();
  }, [longPressCallbacks]);

  const onTouchEnd = useCallback(() => {
    isPotentialLongPress.current = false;
    longPressCallbacks.onTouchEnd();
  }, [longPressCallbacks]);

  return {
    onMouseDown,
    onTouchStart,
    onTouchMove,
    onMouseUp,
    onMouseLeave,
    onTouchEnd,
  };
};

const useDateFormatter = (dateString?: string | null): string | undefined => {
  const isMounted = useIsMounted();
  const [formattedDate, setDate] = useState<string | undefined>(
    formatDate(dateString)
  );
  const timer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const nextFormattedDate = formatDate(dateString);
    setDate(nextFormattedDate);
  }, [dateString]);

  const startInterval = useCallback((): NodeJS.Timeout => {
    const delay = 3000;
    const timer = setInterval(() => {
      if (isMounted()) {
        setDate(formatDate(dateString));
      }
    }, delay);
    return timer;
  }, [isMounted, dateString]);

  const stopInterval = (timer?: NodeJS.Timeout): void => {
    if (timer) {
      clearInterval(timer);
    }
  };

  useEffect(() => {
    stopInterval(timer.current);
    timer.current = startInterval();
  }, [startInterval, dateString]);

  useIonViewDidEnter(() => {
    if (!timer.current) {
      timer.current = startInterval();
    }
  });

  useIonViewDidLeave(() => {
    stopInterval(timer.current);
    timer.current = undefined;
  });

  return formattedDate;
};

export {
  useIsMounted,
  usePrevious,
  useDeepCompareMemo,
  useIsViewing,
  useLocationHash,
  useCancellableLongPress,
  useDateFormatter,
};
